import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, CardHeader, Container } from "reactstrap";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import { ColumnDef } from "@tanstack/react-table";
import { CodeCell, IndexColumn } from "components/common/columns";
import { formatCurrency } from "utils/currency";
import { DateOnly, DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import { PurchaseOrder } from "features/PurchaseOrders/types/purchase-order.types";
import {
  cancelPurchaseOrder,
  confirmPurchaseOrder,
  getPurchaseOrders,
} from "features/PurchaseOrders/services/PurchaseOrderService";
import { PurchaseOrderStatusMap } from "features/PurchaseOrders/constants/purchase-order.constants";
import { toast } from "react-toastify";
import ConfirmModal from "./ApproveModal";
import CancelModal from "./CancelModal";
import { getDocumentTitle } from "utils/helpers";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import PurchaseOrderActionsDropdown from "../components/PurchaseOrderActionsDropdown";

const PurchaseOrderList = () => {
  document.title = getDocumentTitle("Purchase Orders");
  const { can } = useAccess();
  const canCreate = can(Permissions.PURCHASE_ORDER.CREATE);
  const canRead = can(Permissions.PURCHASE_ORDER.READ);
  const canConfirm = can(Permissions.PURCHASE_ORDER.APPROVE);
  const canCancel = can(Permissions.PURCHASE_ORDER.CANCEL);
  const canUpdate = can(Permissions.PURCHASE_ORDER.UPDATE);

  const tableRef = useRef<() => void>(null);
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder | null>(
    null,
  );
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [cancelModal, setCancelModal] = useState<boolean>(false);

  const onClickConfirm = useCallback((record: PurchaseOrder) => {
    setPurchaseOrder(record);
    setConfirmModal(true);
  }, []);

  const onClickCancel = useCallback((record: PurchaseOrder) => {
    setPurchaseOrder(record);
    setCancelModal(true);
  }, []);

  const handleConfirm = async () => {
    if (purchaseOrder) {
      try {
        const response = await confirmPurchaseOrder(purchaseOrder.id);
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
        }
        setConfirmModal(false);
      } catch (err) {
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Confirm failed",
        );
      }
    }
  };

  const handleCancel = async () => {
    if (purchaseOrder) {
      try {
        const response = await cancelPurchaseOrder(purchaseOrder.id);
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
        }
        setCancelModal(false);
      } catch (err) {
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Cancel failed",
        );
      }
    }
  };

  const columns: ColumnDef<PurchaseOrder>[] = useMemo(
    () => [
      IndexColumn<PurchaseOrder>(),
      {
        header: "Code",
        accessorKey: "code",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <Link
            to={`/inventory-management/purchase-orders/${row.original.id}`}
            className="text-decoration-none link"
          >
            <CodeCell code={row.original.code ?? `#${row.original.id}`} />
          </Link>
        ),
      },
      {
        header: "Supplier",
        id: "supplier",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const s = row.original.supplier;
          return s?.name ? (
            <span>{[s.name].filter(Boolean).join(" · ")}</span>
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        header: "Total",
        id: "totalAmount",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const v = row.original.totalAmount;
          return v != null ? (
            <span className="fw-medium">{formatCurrency(v)}</span>
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        header: "Order date",
        id: "orderDate",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const d = row.original.orderDate;
          return d ? (
            <DateOnly date={d} />
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        header: "Status",
        cell: ({ row }) => {
          return (
            <StatusBadge
              value={row.original.status}
              map={PurchaseOrderStatusMap}
            />
          );
        },
        accessorKey: "status",
        enableColumnFilter: false,
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) => {
          return <DateTime date={row.original.created_at ?? ""} />;
        },
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          return (
            <PurchaseOrderActionsDropdown
              record={record}
              canRead={canRead}
              canUpdate={canUpdate}
              canConfirm={canConfirm}
              canCancel={canCancel}
              onConfirm={onClickConfirm}
              onCancel={onClickCancel}
            />
          );
        },
      },
    ],
    [canConfirm, canCancel, canRead, canUpdate, onClickConfirm, onClickCancel],
  );

  return (
    <React.Fragment>
      <ConfirmModal
        show={confirmModal}
        onConfirmClick={handleConfirm}
        onCloseClick={() => setConfirmModal(false)}
        record={purchaseOrder}
      />
      <CancelModal
        show={cancelModal}
        onCancelClick={handleCancel}
        onCloseClick={() => setCancelModal(false)}
        record={purchaseOrder}
      />

      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Purchase Orders"
            pageTitle="Inventory Management"
          />
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="card-title mb-0">Purchase orders</h5>
              {canCreate ? (
                <Link
                  to="/inventory-management/purchase-orders/create"
                  className="btn btn-primary "
                >
                  <i className="ri-add-line align-bottom me-1" />
                  New purchase order
                </Link>
              ) : null}
            </CardHeader>
            <CardBody className="pt-0">
              <TableContainer
                columns={columns}
                fetchApi={getPurchaseOrders}
                pageSize={10}
                globalSearch={true}
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-3"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PurchaseOrderList;
